/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Availability grid — GET /re/projects/{id}/inventory-grid.
 *
 * One request per project; every tile renders from that payload. Tiles are a
 * memoised component with a stable click handler, and filters dim
 * non-matching units instead of removing them so the floor layout stays
 * readable. Comfortable at 500+ units.
 */

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Divider, Drawer, Grid, Icon, IconButton, Skeleton, Tab, Tabs, TextField, Tooltip,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { usePermission } from 'auth/usePermission';
import { FilterChip } from 'components/v2/filters';
import { Building2 } from 'lucide-react';
import colors from 'styles/tokens/colors';
import { v2 as t, v2Density as d, V2_DISPLAY_FONT_FAMILY } from 'styles/tokens/v2';
import RePage, { FilterBar, LoadError, RePanel, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import HoldDialog from './components/HoldDialog';
import { RePicker, VocabSelect } from './components/Pickers';
import { KV } from './components/Widgets';
import { UNIT_TILE_COLORS } from './components/statusColors';
import {
  UNIT_CATEGORIES, UNIT_STATUSES, bedroomsLabel, formatDateTime, formatMoney, formatNumber, humanize, toNumber,
  useReApi, useReGet,
} from './api';
import { drawerPaperSx } from './components/drawerPaper';
import MediaGallery from '../components/MediaGallery';
import { defaultCurrency } from '../variant';
import { useIsReV2 } from '../v2/parts';

const ALL = '__all__';

// UI v2 tile: 8px radius, 1px status border, fixed 13/11px type (the classic
// rem sizes assumed the 80% zoom v2 removes), full-strength text for AA, and
// the v2 focus ring. Same colours, same button, same handler.
const tileSxV2 = (c, dimmed) => ({
  width: 84,
  minHeight: 56,
  px: 0.75,
  py: 0.625,
  borderRadius: `${d.radiusPx}px`,
  border: `1px solid ${c.border}`,
  bgcolor: c.bg,
  color: c.fg,
  cursor: 'pointer',
  opacity: dimmed ? 0.2 : 1,
  textAlign: 'left',
  font: 'inherit',
  fontVariantNumeric: 'tabular-nums',
  transition: 'border-color 120ms, box-shadow 120ms',
  '&:hover': { boxShadow: `inset 0 0 0 1px ${c.border}` },
  '&:focus-visible': { outline: `2px solid ${t.link}`, outlineOffset: 2 },
  '& > div:first-of-type': { fontSize: 13, fontWeight: 700, lineHeight: 1.25 },
  '& > div:not(:first-of-type)': { fontSize: 11, lineHeight: 1.3, opacity: 1 },
});

const UnitTile = memo(({ unit, dimmed, onSelect }) => {
  const isV2 = useIsReV2();
  const c = UNIT_TILE_COLORS[unit.status] || UNIT_TILE_COLORS.SOLD;
  return (
    <Tooltip
      arrow
      disableInteractive
      title={`${unit.unit_number} · ${humanize(unit.status)} · ${bedroomsLabel(unit.bedrooms)} · ${formatMoney(unit.list_price, defaultCurrency(), { decimals: 0 })}`}
    >
      <MDBox
        component="button"
        type="button"
        onClick={() => onSelect(unit)}
        aria-label={isV2 ? `Unit ${unit.unit_number}, ${humanize(unit.status)}` : undefined}
        sx={isV2 ? tileSxV2(c, dimmed) : {
          width: 76,
          minHeight: 50,
          p: 0.5,
          borderRadius: '6px',
          border: `1.5px solid ${c.border}`,
          bgcolor: c.bg,
          color: c.fg,
          cursor: 'pointer',
          opacity: dimmed ? 0.18 : 1,
          textAlign: 'left',
          font: 'inherit',
          transition: 'transform 80ms ease',
          '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 2px 6px rgba(14,35,72,0.15)' },
          '&:focus-visible': { outline: `2px solid ${colors.brand.primary}`, outlineOffset: 1 },
        }}
      >
        <MDBox component="div" sx={{ fontSize: '0.78rem', fontWeight: 700, lineHeight: 1.2 }}>{unit.unit_number}</MDBox>
        <MDBox component="div" sx={{ fontSize: '0.65rem', lineHeight: 1.25, opacity: 0.85 }}>
          {unit.bedrooms === 0 ? 'Studio' : unit.bedrooms != null ? `${unit.bedrooms}BR` : humanize(unit.category)}
        </MDBox>
        <MDBox component="div" sx={{ fontSize: '0.65rem', lineHeight: 1.25, opacity: 0.85 }}>
          {formatMoney(unit.list_price, defaultCurrency(), { compact: true }).replace(`${defaultCurrency()} `, '')}
        </MDBox>
      </MDBox>
    </Tooltip>
  );
});

UnitTile.displayName = 'UnitTile';

const FloorRow = memo(({ floor, matches, onSelect }) => {
  const isV2 = useIsReV2();
  return (
  <MDBox display="flex" alignItems="flex-start" gap={1.5} py={0.75} sx={{ borderBottom: isV2 ? `1px solid ${t.border}` : '1px dashed #E8EDF5' }}>
    <MDBox sx={{ width: 56, flexShrink: 0, pt: 1.5 }}>
      <MDTypography
        variant="caption"
        fontWeight="bold"
        color="text"
        sx={isV2 ? { fontSize: 12, fontWeight: 600, color: `${t.text2} !important`, fontVariantNumeric: 'tabular-nums' } : undefined}
      >
        {floor.floor === null || floor.floor === undefined ? '—' : `Floor ${floor.floor}`}
      </MDTypography>
    </MDBox>
    <MDBox display="flex" flexWrap="wrap" gap={0.75}>
      {floor.units.map((u) => (
        <UnitTile key={u.id} unit={u} dimmed={!matches(u)} onSelect={onSelect} />
      ))}
    </MDBox>
  </MDBox>
  );
});

FloorRow.displayName = 'FloorRow';

function UnitDrawer({ unit, onClose, onChanged, notify }) {
  const navigate = useNavigate();
  const { api } = useReApi();
  const { canCreate: canBook } = usePermission('re_bookings');
  const { canEdit: canEditUnit } = usePermission('re_units');
  const detail = useReGet(unit ? `/re/units/${unit.id}` : null);
  const [holdOpen, setHoldOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const u = { ...(unit || {}), ...(detail.data || {}) };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/re/units/${unit.id}/status`, { status });
      notify(`Unit ${unit.unit_number} is now ${humanize(status)}`);
      onChanged();
      detail.reload();
    } catch (e) {
      notify.error(e, 'Could not change unit status');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer anchor="right" open={Boolean(unit)} onClose={onClose} PaperProps={{ sx: drawerPaperSx({ xs: '100%', sm: 480 }) }}>
      {unit && (
        <MDBox p={2.5} display="flex" flexDirection="column" gap={1.5}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDBox>
              <MDTypography variant="h5" fontWeight="bold">{`Unit ${u.unit_number}`}</MDTypography>
              <MDTypography variant="caption" color="text">{[u.building_name, u.project_name].filter(Boolean).join(' · ')}</MDTypography>
            </MDBox>
            <IconButton onClick={onClose}><Icon>close</Icon></IconButton>
          </MDBox>
          <MDBox><ReStatusChip vocab="unit" status={u.status} /></MDBox>
          {u.status === 'HELD' && u.hold_expires_at && (
            <Alert severity="warning">{`On hold until ${formatDateTime(u.hold_expires_at)}`}</Alert>
          )}
          <MediaGallery parentTable="re_units" parentId={unit.id} canEdit={canEditUnit} />
          <Divider />
          {detail.loading && !detail.data ? <Skeleton variant="rectangular" height={160} /> : (
            <MDBox>
              <KV label="List price" value={formatMoney(u.list_price, u.currency || defaultCurrency())} bold />
              <KV label="Price / sq ft" value={formatMoney(u.price_per_sqft, u.currency || defaultCurrency())} />
              <KV label="Type" value={u.unit_type_name || humanize(u.category)} />
              <KV label="Bedrooms" value={bedroomsLabel(u.bedrooms)} />
              <KV label="Bathrooms" value={u.bathrooms ?? '—'} />
              <KV label="BUA" value={u.bua_sqft ? `${formatNumber(u.bua_sqft)} sq ft` : '—'} />
              {u.plot_sqft && <KV label="Plot" value={`${formatNumber(u.plot_sqft)} sq ft`} />}
              <KV label="Floor" value={u.floor ?? '—'} />
              <KV label="View" value={u.view || '—'} />
              <KV label="Parking" value={u.parking_spaces ?? '—'} />
              {u.service_charge_per_sqft && <KV label="Service charge" value={`${formatMoney(u.service_charge_per_sqft)} / sq ft`} />}
            </MDBox>
          )}
          <Divider />
          <MDBox display="flex" flexWrap="wrap" gap={1}>
            {u.status === 'AVAILABLE' && canBook && (
              <MDButton variant="gradient" color="warning" size="small" onClick={() => setHoldOpen(true)} disabled={busy}>
                <Icon>pause_circle</Icon>&nbsp;Hold
              </MDButton>
            )}
            {['AVAILABLE', 'HELD'].includes(u.status) && canBook && (
              <MDButton variant="gradient" color="info" size="small" onClick={() => navigate(`/real-estate/bookings/new?unit_id=${u.id}`)}>
                <Icon>receipt_long</Icon>&nbsp;Book
              </MDButton>
            )}
            {u.status === 'AVAILABLE' && canEditUnit && (
              <MDButton variant="outlined" color="error" size="small" onClick={() => setStatus('BLOCKED')} disabled={busy}>
                <Icon>block</Icon>&nbsp;Block
              </MDButton>
            )}
            {u.status === 'BLOCKED' && canEditUnit && (
              <MDButton variant="outlined" color="success" size="small" onClick={() => setStatus('AVAILABLE')} disabled={busy}>
                <Icon>lock_open</Icon>&nbsp;Unblock
              </MDButton>
            )}
            {u.booking_id && (
              <MDButton variant="outlined" color="dark" size="small" onClick={() => navigate(`/real-estate/bookings/${u.booking_id}`)}>
                Booking
              </MDButton>
            )}
            <MDButton variant="text" color="info" size="small" onClick={() => navigate(`/real-estate/units/${u.id}`)}>
              Open unit&nbsp;<Icon>open_in_new</Icon>
            </MDButton>
          </MDBox>
          <HoldDialog
            open={holdOpen}
            unit={u}
            onClose={() => setHoldOpen(false)}
            onDone={() => {
              setHoldOpen(false);
              notify(`Unit ${u.unit_number} is on hold`);
              onChanged();
              detail.reload();
            }}
          />
        </MDBox>
      )}
    </Drawer>
  );
}

/** v2 legend chip label: the tile swatch (so the colour key stays) and the status. */
function LegendLabel({ status }) {
  const c = UNIT_TILE_COLORS[status];
  return (
    <MDBox component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <MDBox component="span" aria-hidden sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: c.bg, border: `1px solid ${c.border}`, flexShrink: 0 }} />
      {humanize(status)}
    </MDBox>
  );
}

LegendLabel.propTypes = { status: PropTypes.string.isRequired };

export default function InventoryGrid() {
  const isV2 = useIsReV2();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = searchParams.get('project_id') ? Number(searchParams.get('project_id')) : null;
  const [snack, notify] = useSnack();
  const grid = useReGet(projectId ? `/re/projects/${projectId}/inventory-grid` : null);
  const [buildingTab, setBuildingTab] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState([]);
  const [bedrooms, setBedrooms] = useState(null);
  const [category, setCategory] = useState(null);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => { setBuildingTab(ALL); }, [projectId]);

  const buildings = useMemo(() => grid.data?.buildings || [], [grid.data]);

  const statusCounts = useMemo(() => {
    if (grid.data?.status_counts) return grid.data.status_counts;
    const counts = {};
    buildings.forEach((b) => b.floors.forEach((f) => f.units.forEach((u) => {
      counts[u.status] = (counts[u.status] || 0) + 1;
    })));
    return counts;
  }, [grid.data, buildings]);

  const bedroomOptions = useMemo(() => {
    const set = new Set();
    buildings.forEach((b) => b.floors.forEach((f) => f.units.forEach((u) => {
      if (u.bedrooms !== null && u.bedrooms !== undefined) set.add(Number(u.bedrooms));
    })));
    return [...set].sort((a, b) => a - b);
  }, [buildings]);

  const lo = toNumber(minPrice);
  const hi = toNumber(maxPrice);
  const matches = useCallback((u) => {
    if (statusFilter.length && !statusFilter.includes(u.status)) return false;
    if (bedrooms !== null && Number(u.bedrooms) !== Number(bedrooms)) return false;
    if (category && u.category !== category) return false;
    const p = toNumber(u.list_price);
    if (lo !== null && (p === null || p < lo)) return false;
    if (hi !== null && (p === null || p > hi)) return false;
    return true;
  }, [statusFilter, bedrooms, category, lo, hi]);

  const matchCount = useMemo(() => {
    let n = 0;
    buildings.forEach((b) => b.floors.forEach((f) => f.units.forEach((u) => { if (matches(u)) n += 1; })));
    return n;
  }, [buildings, matches]);

  const visibleBuildings = buildingTab === ALL ? buildings : buildings.filter((b) => String(b.id) === buildingTab);

  const onSelect = useCallback((u) => setSelected(u), []);
  const onChanged = grid.reload;

  const toggleStatus = (s) => setStatusFilter((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  const filtersActive = statusFilter.length || bedrooms !== null || category || minPrice || maxPrice;

  return (
    <RePage title="Inventory" subtitle={grid.data?.project?.name || 'Availability by building and floor'}>
      <FilterBar>
        <RePicker
          path="/re/projects"
          label="Project"
          value={projectId}
          onChange={(id) => setSearchParams(id ? { project_id: String(id) } : {})}
          sx={{ minWidth: 280 }}
        />
        <VocabSelect
          label="Bedrooms"
          value={bedrooms}
          onChange={setBedrooms}
          values={bedroomOptions}
          labelFor={bedroomsLabel}
          sx={{ minWidth: 130 }}
        />
        <VocabSelect label="Category" value={category} onChange={setCategory} values={UNIT_CATEGORIES} sx={{ minWidth: 150 }} />
        {isV2 ? (
          <>
            {/* v2 filter row: 36px inputs, no floating label (placeholder + accessible name). */}
            <TextField type="number" placeholder="Min price" inputProps={{ 'aria-label': 'Min price', min: 0 }} value={minPrice} onChange={(e) => setMinPrice(e.target.value)} sx={{ width: 140 }} />
            <TextField type="number" placeholder="Max price" inputProps={{ 'aria-label': 'Max price', min: 0 }} value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} sx={{ width: 140 }} />
          </>
        ) : (
          <>
            <TextField size="small" type="number" label="Min price" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} sx={{ width: 140 }} />
            <TextField size="small" type="number" label="Max price" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} sx={{ width: 140 }} />
          </>
        )}
        {filtersActive ? (
          <MDButton
            variant="text"
            color="secondary"
            size="small"
            onClick={() => { setStatusFilter([]); setBedrooms(null); setCategory(null); setMinPrice(''); setMaxPrice(''); }}
          >
            Clear filters
          </MDButton>
        ) : null}
      </FilterBar>

      {!projectId && (
        <RePanel>
          <MDTypography variant="button" color="text">Choose a project to see its availability grid.</MDTypography>
        </RePanel>
      )}

      <LoadError error={grid.error} onRetry={grid.reload} />

      {projectId && (
        <RePanel p={0}>
          {/* Legend doubles as the status filter */}
          {isV2 ? (
            <MDBox
              role="group"
              aria-label="Filter by unit status"
              display="flex"
              flexWrap="wrap"
              gap={1}
              p={2}
              alignItems="center"
              sx={{ borderBottom: `1px solid ${t.border}` }}
            >
              {UNIT_STATUSES.map((s) => (
                <FilterChip
                  key={s}
                  label={<LegendLabel status={s} />}
                  selected={statusFilter.includes(s)}
                  onClick={() => toggleStatus(s)}
                  count={formatNumber(statusCounts[s] || 0)}
                />
              ))}
              <MDBox flex={1} />
              {filtersActive ? (
                <MDBox component="span" sx={{ fontSize: 13, color: t.text3, fontVariantNumeric: 'tabular-nums' }}>
                  {`${formatNumber(matchCount)} units match`}
                </MDBox>
              ) : null}
            </MDBox>
          ) : (
          <MDBox display="flex" flexWrap="wrap" gap={1} p={2} alignItems="center" sx={{ borderBottom: '1px solid #E8EDF5' }}>
            {UNIT_STATUSES.map((s) => {
              const c = UNIT_TILE_COLORS[s];
              const active = statusFilter.includes(s);
              return (
                <MDBox
                  key={s}
                  component="button"
                  type="button"
                  onClick={() => toggleStatus(s)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: 999,
                    border: `1.5px solid ${active ? c.border : '#E0E6EF'}`, bgcolor: active ? c.bg : '#fff',
                    cursor: 'pointer', font: 'inherit', fontSize: '0.75rem', color: '#26344A',
                  }}
                >
                  <MDBox component="span" sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: c.bg, border: `1.5px solid ${c.border}` }} />
                  {humanize(s)}
                  <MDBox component="span" sx={{ fontWeight: 700 }}>{statusCounts[s] || 0}</MDBox>
                </MDBox>
              );
            })}
            <MDBox flex={1} />
            {filtersActive ? (
              <MDTypography variant="caption" color="text">{`${formatNumber(matchCount)} units match`}</MDTypography>
            ) : null}
          </MDBox>
          )}

          {buildings.length > 1 && (
            <Tabs
              value={buildingTab}
              onChange={(e, v) => setBuildingTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2, pt: 1 }}
            >
              <Tab value={ALL} label="All buildings" />
              {buildings.map((b) => <Tab key={String(b.id)} value={String(b.id)} label={b.name} />)}
            </Tabs>
          )}

          <MDBox p={2}>
            {grid.loading && !grid.data && (
              <Grid container spacing={1}>
                {Array.from({ length: 24 }).map((_, i) => (
                  <Grid item key={i}><Skeleton variant="rounded" width={76} height={50} /></Grid>
                ))}
              </Grid>
            )}
            {grid.data && buildings.length === 0 && (
              <MDTypography variant="button" color="text">This project has no units yet.</MDTypography>
            )}
            {visibleBuildings.map((b) => (
              <MDBox key={String(b.id)} mb={3}>
                {isV2 ? (
                  <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                    <Building2 size={16} aria-hidden="true" color={t.text2} />
                    <MDBox component="h3" sx={{ m: 0, fontFamily: V2_DISPLAY_FONT_FAMILY, fontSize: 15, fontWeight: 600, color: t.text }}>{b.name}</MDBox>
                    <MDBox component="span" sx={{ fontSize: 13, color: t.text3, fontVariantNumeric: 'tabular-nums' }}>
                      {`${b.floors.reduce((n, f) => n + f.units.length, 0)} units`}
                    </MDBox>
                  </MDBox>
                ) : (
                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                  <Icon sx={{ color: colors.text.light }}>apartment</Icon>
                  <MDTypography variant="h6">{b.name}</MDTypography>
                  <MDTypography variant="caption" color="text">
                    {`${b.floors.reduce((n, f) => n + f.units.length, 0)} units`}
                  </MDTypography>
                </MDBox>
                )}
                {b.floors.map((f) => (
                  <FloorRow key={`${b.id}-${f.floor}`} floor={f} matches={matches} onSelect={onSelect} />
                ))}
              </MDBox>
            ))}
          </MDBox>
        </RePanel>
      )}

      <UnitDrawer unit={selected} onClose={() => setSelected(null)} onChanged={onChanged} notify={notify} />
      {snack}
    </RePage>
  );
}
